import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AdminAddon } from '@/hooks/useAdminCatalog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tutti gli addon esistenti per derivare versione precedente */
  allAddons: AdminAddon[];
}

const BRAND_OPTIONS = [
  { value: 'saint_gobain', label: 'Saint-Gobain' },
  { value: 'knauf', label: 'Knauf' },
  { value: 'fassa', label: 'Fassa Bortolo' },
];

/**
 * F26 Fase D — Crea la bozza dei 3 prodotti (Listino + Strat + Bundle) per un nuovo anno.
 *
 * Flow MVP:
 *  1. Admin seleziona brand + anno (es. SG, 2027)
 *  2. Verifichiamo che non esistano già per quell'anno
 *  3. Clone delle 3 righe library_addons dalla versione corrente (is_current_year=true)
 *  4. La nuova versione nasce con is_active=false e is_current_year=false (per non sovrascrivere quella corrente)
 *  5. Dopo l'admin deve:
 *     a) Caricare i materiali via wizard Excel separato (TODO)
 *     b) Creare i 3 product/variant su LS dashboard
 *     c) Aggiornare lemonsqueezy_variant_id sulle 3 nuove righe
 *     d) Quando il contenuto è pronto: is_active=true + is_current_year=true (e la versione precedente diventa is_current_year=false)
 */
export default function NewVersionDialog({ open, onOpenChange, allAddons }: Props) {
  const [brand, setBrand] = useState<string>('saint_gobain');
  const [year, setYear] = useState<number>(new Date().getFullYear() + 1);
  const [isCreating, setIsCreating] = useState(false);
  const qc = useQueryClient();

  const existing = allAddons.filter((a) => a.brand_slug === brand && a.version_year === year);
  const hasConflict = existing.length > 0;
  const previous = allAddons.filter((a) => a.brand_slug === brand && a.is_current_year);

  const handleCreate = async () => {
    if (hasConflict) {
      toast.error('Versione già esistente', { description: `${brand} ${year} esiste già nel catalogo.` });
      return;
    }
    if (previous.length === 0) {
      toast.error('Versione precedente non trovata', { description: `Nessuna versione corrente per ${brand}.` });
      return;
    }
    setIsCreating(true);
    try {
      // Per ognuna delle 3 righe della versione corrente (Listino / Strat / Bundle),
      // creo una nuova riga col nuovo version_year e supersedes_addon_id puntato al vecchio.
      const newRows = previous.map((prev) => ({
        slug: `${prev.slug.replace(/_\d{4}$/, '')}_${year}`,
        name: prev.name.replace(/\d{4}/, String(year)),
        short_description: prev.name + ' versione ' + year,
        long_description: 'Bozza nuova versione ' + year + '. Materiali e contenuto da caricare via wizard Excel.',
        brand: prev.brand,
        brand_slug: prev.brand_slug,
        category: prev.category,
        price_eur: prev.price_eur,
        upgrade_price_eur: prev.upgrade_price_eur,
        starter_category_filter: prev.starter_category_filter
          ? `${prev.starter_category_filter.replace(/_\d{4}$/, '')}_${year}`
          : null,
        preview_count_materials: 0,
        preview_count_stratigraphies: 0,
        is_active: false, // hidden finche' l'admin non ha caricato contenuto + LS variant
        sort_order: prev.sort_order,
        includes_addon_slugs: prev.includes_addon_slugs?.map((s) => `${s.replace(/_\d{4}$/, '')}_${year}`) ?? null,
        version_year: year,
        is_current_year: false, // diventa true SOLO quando l'admin "pubblica" la nuova versione
        supersedes_addon_id: prev.id,
      }));

      const { error } = await supabase.from('library_addons').insert(newRows);
      if (error) throw error;

      toast.success('Bozza creata', {
        description: `${newRows.length} nuove righe (Listino + Strat + Bundle) per ${brand} ${year}. Tutte hidden finche' carichi i materiali e i variant LS.`,
        duration: 8000,
      });
      qc.invalidateQueries({ queryKey: ['admin', 'addons'] });
      qc.invalidateQueries({ queryKey: ['library_addons'] });
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error('Errore creazione bozza', { description: msg });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Crea bozza nuova versione</DialogTitle>
          <DialogDescription>
            Crea le 3 righe template (Listino, Stratigrafie, Bundle) per il nuovo anno. Saranno hidden finché non avrai caricato il contenuto e collegato i variant Lemon Squeezy.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="brand">Brand</Label>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger id="brand"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BRAND_OPTIONS.map((b) => (
                  <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="year">Anno listino</Label>
            <Input
              id="year"
              type="number"
              min={2026}
              max={2099}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </div>

          {previous.length > 0 && (
            <div className="text-xs text-zinc-500 bg-zinc-50 rounded p-2">
              <strong>Cloneremo:</strong>
              <ul className="mt-1 list-disc list-inside">
                {previous.map((p) => (
                  <li key={p.id}>{p.name} <span className="font-mono text-[10px]">({p.slug})</span></li>
                ))}
              </ul>
            </div>
          )}

          {hasConflict && (
            <div className="flex items-start gap-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Esiste già una versione {year} per {brand} ({existing.length} righe). Cambia anno o cancella prima i record esistenti.</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleCreate} disabled={isCreating || hasConflict}>
            {isCreating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Crea bozza
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
